import React from 'react';
import { Container } from 'react-bootstrap';
import PageWrapper from './common/PageWrapper';
import './Info.css';
import { useLocalization } from '../localization';

type TransportMode = 'train' | 'metro' | 'bus' | 'night';

interface TransportItem {
  mode: TransportMode;
  service: string;
  line: string;
  description: string;
  badges?: string[];
  badgeClassName?: string;
  logoUrl?: string;
  logoAlt?: string;
}

const transportItems: Omit<TransportItem, 'description'>[] = [
  {
    mode: 'train',
    service: 'FGC',
    line: '',
    badges: ['L8', 'R5', 'R6', 'R50', 'R60', 'S3', 'S4', 'S8', 'S9'],
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/a/a4/FGC_logo_%282019%29.svg',
    logoAlt: 'FGC',
  },
  {
    mode: 'metro',
    service: 'Metro',
    line: 'L1',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/8/81/Barcelona_Metro_Logo.svg',
    logoAlt: 'Metro de Barcelona',
  },
  {
    mode: 'metro',
    service: 'Metro',
    line: 'L9S',
    badgeClassName: 'transport-line-metro-l9s',
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/8/81/Barcelona_Metro_Logo.svg',
    logoAlt: 'Metro de Barcelona',
  },
  {
    mode: 'bus',
    service: 'Bus',
    line: 'M12',
    badgeClassName: 'transport-line-bus-m12',
  },
  {
    mode: 'night',
    service: 'Nitbus',
    line: 'N13',
  },
];

const TransportIcon: React.FC<{ item: Omit<TransportItem, 'description'> }> = ({ item }) => {
  if (item.logoUrl) {
    return (
      <img
        src={item.logoUrl}
        alt={item.logoAlt ?? item.service}
        className={`transport-icon-logo transport-icon-logo-${item.mode}`}
      />
    );
  }

  const { mode } = item;

  if (mode === 'bus' || mode === 'night') {
    return (
      <svg viewBox="0 0 512 512" aria-hidden="true" className={`transport-icon-svg transport-icon-svg-${mode}`}>
        <path fill="currentColor" d="M400,32H112A48,48,0,0,0,64,80V400a47.91,47.91,0,0,0,16,35.74V454a26,26,0,0,0,26,26h28a26,26,0,0,0,26-26v-6H352v6a26,26,0,0,0,26,26h28a26,26,0,0,0,26-26V435.74A47.91,47.91,0,0,0,448,400V80A48,48,0,0,0,400,32ZM147.47,399.82a32,32,0,1,1,28.35-28.35A32,32,0,0,1,147.47,399.82ZM236,288H112a16,16,0,0,1-16-16V144a16,16,0,0,1,16-16H236a4,4,0,0,1,4,4V284A4,4,0,0,1,236,288ZM256,96H112.46c-8.6,0-16-6.6-16.44-15.19A16,16,0,0,1,112,64H399.54c8.6,0,16,6.6,16.44,15.19A16,16,0,0,1,400,96H256Zm20,32H400a16,16,0,0,1,16,16V272a16,16,0,0,1-16,16H276a4,4,0,0,1-4-4V132A4,4,0,0,1,276,128Zm60.18,243.47a32,32,0,1,1,28.35,28.35A32,32,0,0,1,336.18,371.47Z" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="transport-icon-svg">
      <rect x="6" y="3.5" width="12" height="13" rx="3" fill="currentColor" />
      <rect x="8" y="6" width="8" height="4.5" rx="1" fill="white" />
      <path d="M9 18h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M10 16.5l-2 3M14 16.5l2 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
};

const Info: React.FC = () => {
  const { copy } = useLocalization();
  const transportDescriptions = [
    copy.info.fgcDescription,
    copy.info.metroL1Description,
    copy.info.metroL9Description,
    copy.info.busDescription,
    copy.info.nightBusDescription,
  ];

  return (
    <Container>
      <PageWrapper title={copy.info.title} containerClassName="page-container-complete-border">
        <div className="info-page">
          <section className="info-block info-map-block" aria-labelledby="mapa-title">
            <h3 id="mapa-title" className="info-heading">{copy.info.directions}</h3>
            <div className="info-map-frame">
              <iframe
                loading="lazy"
                src="https://maps.google.com/maps?q=el%20pumarejo&amp;t=m&amp;z=15&amp;output=embed&amp;iwloc=near"
                title={copy.info.mapTitle}
                aria-label={copy.info.mapAriaLabel}
              />
            </div>
             <div className="info-address-layout">
              <div className="info-address-photo">
                <img
                  src="/img/streetview_petit.png"
                  alt={copy.info.facadeAlt}
                />
              </div>
              <div className="info-address-copy">
                <div className="info-address-card">
                  <p className="info-venue-name">EL PUMAREJO</p>
                  <p>Av. Carrilet, 187 – Nau 4</p>
                  <p>08907 L&apos;Hospitalet de Llobregat</p>
                  <p className="info-address-note">{copy.info.addressNote}</p>
                </div>
              </div>
            </div>
            <div className="transport-list">
              {transportItems.map((item, index) => (
                <div key={`${item.service}-${item.line}`} className="transport-item">
                  <div className={`transport-icon transport-icon-${item.mode}`}>
                    <TransportIcon item={item} />
                  </div>
                  <div className="transport-copy">
                    <div className="transport-meta">
                      <span className="transport-service">{item.service}</span>
                      {item.badges?.length ? (
                        <div className="transport-badge-list" aria-label={`${item.service} lines`}>
                          {item.badges.map((badge) => (
                            <span
                              key={badge}
                              className={`transport-line transport-line-${item.mode} transport-line-train-${badge.toLowerCase()}`}
                            >
                              {badge}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className={`transport-line transport-line-${item.mode} ${item.badgeClassName ?? ''}`.trim()}>{item.line}</span>
                      )}
                    </div>
                    <p>{transportDescriptions[index]}</p>
                  </div>
                </div>
              ))}
            </div>
          </section><section className="info-block info-mails-block" aria-labelledby="mails-title">
            <h3 id="mails-title" className="info-heading">{copy.info.contact}</h3>
            <div className="info-mails">
              <p>{copy.info.contactInfo} <a href="mailto:info@elpumarejo.com">info@elpumarejo.com</a>.</p>
              <p>{copy.info.contactBooking} <a href="mailto:booking@elpumarejo.com">booking@elpumarejo.com</a>.</p>
              <p>{copy.info.contactNotice}</p>
            </div>
          </section><section className="info-block info-past-block" aria-labelledby="past-title">
            <h3 id="past-title" className="info-heading">{copy.info.pastEvents}</h3>
            <div className="info-past">
              <p>{copy.info.pastLink} <a href="/past">{copy.info.here}</a>.</p>
            </div>
          </section><section className="info-block info-web-problems-block" aria-labelledby="web-problems-title">
            <h3 id="web-problems-title" className="info-heading">{copy.info.webProblems}</h3>
            <div className="info-past">
              <p>{copy.info.webProblemsNotice} <a href="mailto:jojo@elpumarejo.com">jojo@elpumarejo.com</a>.</p>
            </div>
          </section>
        </div>
      </PageWrapper>
    </Container>
  );
};

export default Info;
